import { Button, ConfirmModal, DrawerFooter } from "@/components";
import {
  FormDatePicker,
  FormDropzoneMultiple,
  FormInput,
  FormRadioGroup,
  FormSelectMultiple,
} from "@/components/Form";
import { DROPZONE_ACCEPT } from "@/constants/constants";
import { updateNotification } from "@/services";
import { useCompanyStore } from "@/store/companyStore";
import { useNotificationStore } from "@/store/notificationStore";
import {
  Notification,
  NOTIFICATION_QUERY_KEYS,
  NotificationStatus,
  RoleEnum,
  SendToAllEnum,
  UpdateNotificationParams,
} from "@/types";
import {
  formatDate,
  jsonToFormData,
  toastApiError,
  toastSuccess,
} from "@/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isNil } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { GlobalError } from "../components";
import { useFormNotification } from "../hooks";
import { NewNotificationSchemaType } from "../schemas";

type Props = {
  onClose: () => void;
  id: number | null; // Id của notification khi edit
  initialData?: Notification;
  role: RoleEnum; // check role to display form correctly
};

const FormNotification: React.FC<Props> = ({
  onClose,
  id,
  initialData,
  role,
}) => {
  const [open, setOpen] = useState(false);
  const { reset } = useNotificationStore();
  const queryClient = useQueryClient();

  // Reset form when modal is closed
  useEffect(() => {
    return reset;
  }, []);

  const closeAll = () => {
    setOpen(false);
    onClose();
  };

  const {
    control,
    handleSubmit,
    timeSendNotification,
    options,
    updateSendTimeIfPast,
    isDisplaySelectGroups,
    isDisplaySelectStores,
    isDisplaySelectUsers,
    // search option input
    setSearchGroups,
    setSearchStores,
    setSearchUsers,
    // option groups
    storeGroups,
    fetchNextPageGroups,
    hasNextPageGroups,
    isFetchingGroups,
    // option stores
    myCompanyStores,
    fetchNextPageStores,
    hasNextPageStores,
    isFetchingStores,
    // option users
    storeUsers,
    fetchNextPageUsers,
    hasNextPageUsers,
    isFetchingUsers,
  } = useFormNotification(role, initialData);

  const { company } = useCompanyStore();
  const storeDisplayName = useMemo(() => {
    return company?.store_display_name || "店舗";
  }, [company]);

  const resetApiList = () => {
    queryClient.invalidateQueries({
      queryKey: [NOTIFICATION_QUERY_KEYS.ALL_NOTIFICATIONS],
    });
  };

  // Handle create notification success
  const handleCreateSuccess = (data: any) => {
    toastSuccess(data?.message || "Success");
    resetApiList();
    closeAll();
  };

  const minDate = useMemo(() => {
    return new Date(new Date().getTime() + 60 * 60 * 1000);
  }, []);

  // Process data and call API
  const parseDataToSubmitByRole = useCallback(
    async (
      data: NewNotificationSchemaType,
      role: RoleEnum
    ): Promise<UpdateNotificationParams> => {
      const newData: UpdateNotificationParams = {
        send_time: data.send_time,
        title: data.title,
        description: data.description,
        manager: data.manager,
        status: data.status,
        files: data.fileItems
          ?.map((fileItem) => fileItem.file)
          .filter((file) => file),
        deletedAttachments: data.fileDeletedIds,
      };

      // [SENDER by ROLE]
      // Role case: company admin & head office user
      // Send to: all store groups & all stores (only myself)
      if (
        role === RoleEnum.COMPANY_ADMIN ||
        role === RoleEnum.HEAD_OFFICE_USER
      ) {
        // [SENDER STORES]
        if (data.send_to_all_stores === SendToAllEnum.ALL) {
          newData.send_to_all_stores = 1;
          newData.storeIds = [];
        } else if (data.send_to_all_stores === SendToAllEnum.NONE) {
          newData.send_to_all_stores = 0;
          newData.storeIds = [];
        } else {
          newData.send_to_all_stores = 0;
          newData.storeIds = data.stores?.map((item) => item.value) || []; // get key from option
        }

        // [SENDER GROUPS]
        if (data.send_to_all_groups === SendToAllEnum.ALL) {
          newData.send_to_all_groups = 1;
          newData.groupIds = [];
        } else if (data.send_to_all_groups === SendToAllEnum.NONE) {
          newData.send_to_all_groups = 0;
          newData.groupIds = [];
        } else {
          newData.send_to_all_groups = 0;
          newData.groupIds = data.groups?.map((group) => group.value) || []; // get key from option
        }
      }

      // Role case 2: Store manager
      // Send to: all members in my store
      if (role === RoleEnum.STORE_MANAGER) {
        // [SENDER USERS]
        if (data.send_to_all_users === SendToAllEnum.ALL) {
          newData.send_to_all_users = 1;
          newData.storeUserIds = [];
        } else if (data.send_to_all_users === SendToAllEnum.NONE) {
          newData.send_to_all_users = 0;
          newData.storeUserIds = [];
        } else {
          newData.send_to_all_users = 0;
          newData.storeUserIds =
            data.storeUsers?.map((user) => user.value) || []; // get key from option
        }
      }

      return newData;
    },
    []
  );

  // Handle save notification
  const handleSaveNotification = useCallback(
    async ({
      id,
      data,
      role,
    }: {
      id: number;
      data: NewNotificationSchemaType;
      role: RoleEnum;
    }) => {
      const newData = await parseDataToSubmitByRole(data, role);
      const formData = jsonToFormData(newData);
      return updateNotification(id, formData);
    },
    []
  );

  const { mutateAsync: mutateUpdate, isPending: isPendingUpdate } = useMutation(
    {
      mutationKey: [NOTIFICATION_QUERY_KEYS.UPDATE],
      mutationFn: handleSaveNotification,
      onSuccess: handleCreateSuccess,
      onError: toastApiError,
    }
  );

  const { mutateAsync: mutateUpdateDraft, isPending: isPendingUpdateDraft } =
    useMutation({
      mutationKey: [NOTIFICATION_QUERY_KEYS.UPDATE_DRAFT],
      mutationFn: handleSaveNotification,
      onSuccess: handleCreateSuccess,
      onError: toastApiError,
    });

  // handle button save
  const handleSave = () => {
    updateSendTimeIfPast();
    // validate form, if valid open confirm modal
    handleSubmit(() => {
      setOpen(true);
    })();
  };

  // save
  const save = () => {
    if (isNil(id)) return;

    handleSubmit((data) => {
      data.status = NotificationStatus.READY;
      mutateUpdate({ id, data, role });
    })();
  };

  // save draft
  const saveDraft = () => {
    if (isNil(id)) return;

    handleSubmit((data) => {
      data.status = NotificationStatus.DRAFT;
      mutateUpdateDraft({ id, data, role });
    })();
  };

  // handle form submit
  const handleFormSubmit = () => {
    handleSave();
  };

  // Nếu role là member or other thì disable form, user chỉ view notification
  const disabled = role === RoleEnum.OTHER || role === RoleEnum.USER;

  return (
    <div className="flex flex-col h-full">
      <form
        id="new-notification"
        className="space-y-4 flex-1 p-4 overflow-y-auto"
        onSubmit={handleFormSubmit}
      >
        <FormDatePicker
          control={control}
          name="send_time"
          label="通知日"
          placeholder="YYYY/MM/DD 00:00"
          required
          showTimeSelect
          disabled={disabled}
          dateFormat="yyyy/MM/dd HH:mm"
          minDate={minDate} // + 1h
        />
        <FormInput
          control={control}
          name="title"
          label="通知テーマ"
          placeholder="通知テーマを入力してください。"
          required
          maxLength={150}
          disabled={disabled}
        />
        <FormInput
          control={control}
          name="description"
          label="通知内容"
          placeholder="通知内容を入力してください。"
          multiple
          maxLength={2000}
          required
          disabled={disabled}
        />

        <FormInput
          control={control}
          name="manager"
          label="通知者"
          required
          maxLength={150}
          disabled={disabled}
        />

        {/* Company admin can select group, store to send notification */}
        {role === RoleEnum.COMPANY_ADMIN && (
          <>
            <div className="font-bold leading-6">グループ</div>

            <FormRadioGroup
              options={options}
              control={control}
              name="send_to_all_groups"
              disabled={disabled}
            />

            {isDisplaySelectGroups && (
              <FormSelectMultiple
                options={storeGroups}
                allowSearchInput
                control={control}
                name="groups"
                onLoadMoreItems={fetchNextPageGroups}
                onSearchInputChange={setSearchGroups}
                label="グループ"
                placeholder="グループを選択してください"
                isLoadingMoreItems={isFetchingGroups}
                stopCheckScrollToBottom={!hasNextPageGroups}
                disabled={disabled}
              />
            )}

            <div className="font-bold leading-6">{storeDisplayName}</div>

            <FormRadioGroup
              options={options}
              control={control}
              name="send_to_all_stores"
              disabled={disabled}
            />

            {isDisplaySelectStores && (
              <FormSelectMultiple
                options={myCompanyStores}
                allowSearchInput
                control={control}
                name="stores"
                onLoadMoreItems={fetchNextPageStores}
                onSearchInputChange={setSearchStores}
                label={storeDisplayName}
                placeholder={`${storeDisplayName}を選択してください`}
                isLoadingMoreItems={isFetchingStores}
                stopCheckScrollToBottom={!hasNextPageStores}
                disabled={disabled}
              />
            )}
          </>
        )}

        {/* Head office user can select store to send notification */}
        {role === RoleEnum.HEAD_OFFICE_USER && (
          <>
            <div className="font-bold leading-6">{storeDisplayName}</div>

            <FormRadioGroup
              options={options}
              control={control}
              name="send_to_all_stores"
              disabled={disabled}
            />

            {isDisplaySelectStores && (
              <FormSelectMultiple
                options={myCompanyStores}
                allowSearchInput
                control={control}
                name="stores"
                onLoadMoreItems={fetchNextPageStores}
                onSearchInputChange={setSearchStores}
                label={storeDisplayName}
                placeholder={`${storeDisplayName}を選択してください`}
                isLoadingMoreItems={isFetchingStores}
                stopCheckScrollToBottom={!hasNextPageStores}
                disabled={disabled}
              />
            )}
          </>
        )}

        {/* Store manager can select user to send notification  */}
        {role === RoleEnum.STORE_MANAGER && (
          <>
            <div className="font-bold leading-6">店員</div>

            <FormRadioGroup
              options={options}
              control={control}
              name="send_to_all_users"
              disabled={disabled}
            />

            {isDisplaySelectUsers && (
              <FormSelectMultiple
                options={storeUsers}
                allowSearchInput
                control={control}
                name="storeUsers"
                onLoadMoreItems={fetchNextPageUsers}
                onSearchInputChange={setSearchUsers}
                label="店員"
                placeholder="店員を選択してください"
                isLoadingMoreItems={isFetchingUsers}
                stopCheckScrollToBottom={!hasNextPageUsers}
                disabled={disabled}
                enableOptionDescription
              />
            )}
          </>
        )}

        <GlobalError control={control} />

        <FormDropzoneMultiple
          control={control}
          name="fileItems"
          nameDelete="fileDeletedIds"
          accept={{
            ...DROPZONE_ACCEPT.IMAGE.COMMON,
            ...DROPZONE_ACCEPT.PDF,
            ...DROPZONE_ACCEPT.VIDEO.COMMON,
          }}
          maxSizeInBytes={500 * 1024 * 1024} // 500MB
          errorFileSize="最大アップロードサイズを超過しています。サイズの上限は500MBです。"
          errorInvalidType="画像は MP4, PDF, PNG, JPG, JPGE のうちいずれかの形式のファイルを指定してください。"
          disabled={disabled}
          maxFiles={5}
        />
      </form>

      {!disabled && (
        <DrawerFooter>
          <Button
            type="button"
            buttonType="text"
            color="default"
            onClick={onClose}
            className="!h-12"
          >
            キャンセル
          </Button>
          <Button
            type="button"
            variant="outlined"
            color="default"
            onClick={saveDraft}
            className="!h-12"
            isLoading={isPendingUpdateDraft}
          >
            下書き保存
          </Button>
          <Button
            type="button"
            variant="solid"
            color="primary"
            className="!h-12"
            onClick={handleSave}
          >
            作成
          </Button>
        </DrawerFooter>
      )}

      <ConfirmModal
        type="create"
        open={open}
        onOpenChange={setOpen}
        onConfirm={save}
        title="確認"
        confirmation={
          <p className="text-sm leading-[1.375rem] px-4 py-2 -mx-4">
            {timeSendNotification && formatDate(timeSendNotification)}
            となったら自動通知されます。
            <br />
            よろしいでしょうか。
          </p>
        }
        isPending={isPendingUpdate}
      />
    </div>
  );
};

export default FormNotification;
